import React, { useState, useEffect, useContext } from "react";
import { PhoneField, Textfield } from "../Input/Input";
import StandardFieldset from "../UI/StandardFieldset";
import StandardH2 from "../UI/StandardH2";
import StandardH5 from "../UI/StandardH5";
import { useTranslation } from "react-i18next";
import { googleDataLayer } from "../Analytics/Universal/PageView/googleDataLayer";
import { getDomainConfig } from "../../config/config";
import { ABContext } from "../../contexts/ab-context";
import { ReactComponent as QuestionmarkIcon } from '../../images/global/questionmark-tooltip.svg';
import EmailAutocomplete from "../UI/EmailAutocomplete";
import { publish, subscribe } from "../../utils/events";
import isABTest from "../../ab/isabtest";

const isEmailAutocompleteABTest = isABTest("CHEC-516-adding-email-autocomplete");

const CuxInfo = (props) => {
  const config = getDomainConfig();
  const abContext = useContext(ABContext);
  // CRO-1381-checkout-redesign
  const [isCro1381, setIsCro1381] = useState(false);

  useEffect(() => {
    if ((config.brand !== 'Sw') && abContext.cro1381 !== undefined) setIsCro1381(abContext.cro1381.variant);
  }, [abContext]);
  // END CRO-1381-checkout-redesign

  const { t } = useTranslation('common');

  // Checkbox functionality for main guest
  const { onGuestInfoChange, getValues, register, errors, setValue, touchedFields, guestInfo = {} } = props;
  const [isMainGuestChecked, setIsMainGuestChecked] = useState(guestInfo?.isMainGuestChecked || false);
  const [checkboxErrorMessage, setCheckboxErrorMessage] = useState('');
  const [hasBeenChecked, setHasBeenChecked] = useState(false);

  useEffect(() => {
    setIsMainGuestChecked(guestInfo.isMainGuestChecked);
  }, [guestInfo.isMainGuestChecked])

  const handleGuestboxChange = () => {
    const firstName = getValues("billing-address-fname");
    const lastName = getValues("billing-address-lname");

    if (hasBeenChecked && (firstName === '' || lastName === '' || firstName !== guestInfo.firstName || lastName !== guestInfo.lastName)) {
      setIsMainGuestChecked(false);
      onGuestInfoChange({
        firstName,
        lastName,
        isMainGuestChecked: false
      });
    }
  };

  const handleCheckboxChange = (event) => {
    const firstName = getValues("billing-address-fname");
    const lastName = getValues("billing-address-lname");

    if (!firstName || !lastName) {
      event.preventDefault();
      setCheckboxErrorMessage(`${t('guest-checkbox-validation')}`);
    } else {
      const isChecked = event.target.checked;
      setIsMainGuestChecked(isChecked);
      setHasBeenChecked(true);
      onGuestInfoChange({
        firstName,
        lastName,
        isMainGuestChecked: isChecked
      });
      setCheckboxErrorMessage('');
    }
  };

  useEffect(() => {
    const callback = (e) => {
      props.setValue("email", e.detail, { shouldValidate: true, shouldTouch: true });
    };

    subscribe("email", "updateTextfield", callback);
  }, []);

  return (
    <StandardFieldset id="customer-pane">

      <div className={isCro1381 && ("cro1381CuxHeading")}>
        <StandardH2 className={isCro1381 && ("cro1381H2")}>{t('customer-information')}</StandardH2>
        {isCro1381 && (<StandardH5 className="cro1381ErrorText">(*) {t('required-field')}</StandardH5>)}
      </div>
      {isCro1381 &&
        <>
          <div className="cro1381HalfForm">
            <Textfield
              register={props.register}
              errors={props.errors}
              inputSize={32}
              required={true}
              name={"billing-address-fname"}
              label={t("first-name")}
              errorText={t('please-enter-value-for-firstname')}
              setValue={props.setValue}
              touched={props.touchedFields["billing-address-fname"]}
              maskingClass='gb-mask'
              autocomplete={'given-name'}
              dataLayerInput={() => googleDataLayer.createUserClickInputEvent("billing-address-fname")}
              onChange={handleGuestboxChange}
            />

            <Textfield
              register={props.register}
              errors={props.errors}
              inputSize={32}
              required={true}
              name={"billing-address-lname"}
              label={t("last-name")}
              errorText={t('please-enter-value-for-lastname')}
              setValue={props.setValue}
              touched={props.touchedFields["billing-address-lname"]}
              maskingClass='gb-mask'
              autocomplete={'family-name'}
              dataLayerInput={() => googleDataLayer.createUserClickInputEvent("billing-address-lname")}
              onChange={handleGuestboxChange}
            />
          </div>
          <div className="mainGuest">
            <input type="checkbox" id="mainGuest" checked={isMainGuestChecked} onChange={handleCheckboxChange} />
            <label htmlFor="mainGuest">{config.brand === "Atd" && !props.cartMeta.is_hotel_cart ? t('main-passenger') : t('main-guest')}</label>
          </div>
          {checkboxErrorMessage && <div className="checkboxErrorMessage"><span></span><p>{checkboxErrorMessage}</p></div>}
        </>
      }
      <div className={isCro1381 && ("cro1381FullForm")}>
        <div id="emailWrapper">
          <Textfield
            autocomplete="off"
            register={props.register}
            errors={props.errors}
            inputSize={32}
            required={true}
            name={"email"}
            placeholder={t("youremail-domain-com")}
            pattern={/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/}
            label={t("email-address")}
            errorText={t("please-enter-a-valid-email-address")}
            setValue={props.setValue}
            touched={props.touchedFields['email']}
            maskingClass="gb-mask"
            dataLayerInput={() => googleDataLayer.createUserClickInputEvent('email')}
            type="email"
            onChange={(e) =>{
              publish("email", "onChange", e);
            }}
            onKeyUp={(e) =>{
              publish("email", "onKeyUp", e);
            }}
          />
        {isEmailAutocompleteABTest ? <EmailAutocomplete context="email"/> : null}
        </div>
      </div>
      <div className={isCro1381 && ("cro1381FullForm")}>
        {/* Mention me wrapper */}
        <div id="mmWrapper"></div>
      </div>
      {isCro1381 &&
        <>
          <div className="phoneLabel">
            <label>{t('phone-number')}<span>*</span></label>
            <div className="questionmarkIcon">
              <QuestionmarkIcon />
              <div className="tooltipContainer">
                <div className="tooltipContent">
                  <p>{t('phone-tooltip')}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="cro1381FullForm phoneField">
            <PhoneField
              register={props.register}
              errors={props.errors}
              setError={props.setError}
              clearErrors={props.clearErrors}
              control={props.control}
              required={true}
              icon={true}
              name={"billing-address-phone"}
              label={t('phone-number')}
              errorText={t('please-enter-a-phone-number')}
              placeholder={t('phone-number-placeholder')}
              setValue={props.setValue}
              getValues={props.getValues}
              touched={props.touchedFields["billing-address-phone"]}
              maskingClass='gb-mask'
              autocomplete={'tel'}
              dataLayerInput={() => googleDataLayer.createUserClickInputEvent("billing-address-phone")}
            />
          </div>
        </>
      }
    </StandardFieldset>
  );
};

export default CuxInfo;
